import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart } from "lucide-react";
import api from "@/lib/api";

const slug = (s) => String(s).replace(/[^a-z0-9]/gi, "").toLowerCase();

function Pills({ field, value, onChange, multi }) {
  const arr = Array.isArray(value) ? value : [];
  const max = field.max;
  const toggle = (opt) => {
    if (!multi) return onChange(opt);
    if (arr.includes(opt)) return onChange(arr.filter((x) => x !== opt));
    if (max && arr.length >= max) return; // enforce limit
    onChange([...arr, opt]);
  };
  const isActive = (opt) => (multi ? arr.includes(opt) : value === opt);
  return (
    <div className="flex flex-wrap gap-2">
      {field.options.map((opt) => (
        <button
          key={opt}
          data-testid={`opt-${field.key}-${slug(opt)}`}
          onClick={() => toggle(opt)}
          className={`text-sm font-medium px-4 py-2 rounded-full border-2 transition-colors ${
            isActive(opt) ? "border-[var(--brand)] bg-sage text-ink" : "border-subtle bg-white text-ink-soft"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function Compare() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/checkins?range=60").then((r) => {
      const data = r.data || [];
      const base = data.find((d) => d.baseline) || data[0];
      const latest = [...data].reverse().find((d) => !d.baseline) || data[data.length - 1];
      if (!base || !latest) return setRows([]);
      setRows([
        ["Sleep", base.sleep_score, latest.sleep_score],
        ["Energy", base.energy_score, latest.energy_score],
        ["Mood", base.mood_score, latest.mood_score],
        ["Wellbeing", base.wellbeing_score, latest.wellbeing_score],
      ]);
    }).catch(() => setRows([]));
  }, []);
  if (!rows) return <div className="text-ink-muted text-sm">Loading your numbers…</div>;
  if (rows.length === 0) return <div className="text-ink-muted text-sm">Log a check in to see your comparison.</div>;
  return (
    <div className="bg-app rounded-xl p-4 space-y-2">
      {rows.map(([label, a, b]) => (
        <div key={label} className="flex items-center justify-between text-sm">
          <span className="text-ink-soft">{label}</span>
          <span className="text-ink font-medium">Day 1: {a ?? "–"}/10 → Now: {b ?? "–"}/10</span>
        </div>
      ))}
      <p className="text-ink-muted text-xs pt-1">Your own self reported numbers, not a medical assessment.</p>
    </div>
  );
}

export default function InteractiveField({ field: f, value, onChange }) {
  const navigate = useNavigate();
  return (
    <div data-testid={`interactive-${f.key}`}>
      <label className="text-sm font-semibold text-ink block mb-2">
        {f.label}{f.max ? <span className="text-ink-muted font-normal"> (max {f.max})</span> : null}
      </label>

      {f.type === "score" && (
        <div>
          <div className="flex justify-end mb-1"><span className="font-serif text-xl font-bold text-brand">{value || 5}</span></div>
          <input type="range" min={1} max={10} value={value || 5} onChange={(e) => onChange(Number(e.target.value))} className="reset-slider" data-testid={`score-${f.key}`} />
        </div>
      )}

      {f.type === "select" && <Pills field={f} value={value} onChange={onChange} multi={false} />}
      {(f.type === "multiselect" || f.type === "checklist") && <Pills field={f} value={value} onChange={onChange} multi />}

      {f.type === "text" && (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2}
          className="w-full rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)] resize-none" placeholder="Your answer…" />
      )}

      {f.type === "number" && (
        <input type="number" value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="w-32 rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)]" placeholder="e.g. 20" />
      )}

      {f.type === "time" && (
        <input type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`time-${f.key}`}
          className="rounded-xl border border-subtle bg-app px-4 py-3 text-ink outline-none focus:border-[var(--brand)]" />
      )}

      {f.type === "boolean" && (
        <div className="flex gap-2">
          {["Yes", "No"].map((opt) => (
            <button key={opt} data-testid={`opt-${f.key}-${opt.toLowerCase()}`} onClick={() => onChange(opt)}
              className={`text-sm font-medium px-6 py-2 rounded-full border-2 transition-colors ${value === opt ? "border-[var(--brand)] bg-sage text-ink" : "border-subtle bg-white text-ink-soft"}`}>
              {opt}
            </button>
          ))}
        </div>
      )}

      {f.type === "textlist" && (
        <div className="space-y-2">
          {f.fields.map((sub, i) => (
            <div key={i}>
              <div className="text-xs text-ink-muted mb-1">{sub}</div>
              <input
                data-testid={`textlist-${f.key}-${i}`}
                value={(Array.isArray(value) ? value[i] : "") || ""}
                onChange={(e) => {
                  const arr = Array.isArray(value) ? [...value] : [];
                  arr[i] = e.target.value;
                  onChange(arr);
                }}
                className="w-full rounded-xl border border-subtle bg-app px-4 py-2.5 text-ink outline-none focus:border-[var(--brand)]"
                placeholder="…"
              />
            </div>
          ))}
        </div>
      )}

      {f.type === "budget" && (() => {
        const obj = value && typeof value === "object" ? value : {};
        const total = f.options.reduce((s, c) => s + (Number(obj[c]) || 0), 0);
        return (
          <div className="space-y-3">
            {f.options.map((cat) => (
              <div key={cat}>
                <div className="flex justify-between text-sm mb-1"><span className="text-ink-soft">{cat}</span><span className="font-semibold text-brand">{Number(obj[cat]) || 0}</span></div>
                <input type="range" min={0} max={100} value={Number(obj[cat]) || 0}
                  onChange={(e) => onChange({ ...obj, [cat]: Number(e.target.value) })} className="reset-slider" data-testid={`budget-${f.key}-${slug(cat)}`} />
              </div>
            ))}
            <div className={`text-sm font-semibold ${Math.abs(total - 100) <= 5 ? "text-brand" : "text-accent-c"}`} data-testid={`budget-total-${f.key}`}>
              Total: {total} / 100 {Math.abs(total - 100) <= 5 ? "✓" : "— aim for about 100"}
            </div>
          </div>
        );
      })()}

      {f.type === "meal" && (
        <MealBuilder slots={f.slots} value={value} onChange={onChange} keyName={f.key} />
      )}

      {f.type === "meal_list" && (
        <div className="space-y-4">
          {Array.from({ length: f.count }).map((_, i) => {
            const arr = Array.isArray(value) ? value : [];
            return (
              <div key={i} className="bg-app rounded-xl p-3">
                <div className="text-xs font-semibold text-ink mb-2">Meal {i + 1}</div>
                <MealBuilder
                  slots={f.slots}
                  value={arr[i]}
                  keyName={`${f.key}-${i}`}
                  onChange={(mv) => { const next = [...arr]; next[i] = mv; onChange(next); }}
                />
              </div>
            );
          })}
        </div>
      )}

      {f.type === "compare" && <Compare />}

      {f.type === "trends" && (
        <div className="bg-app rounded-xl p-4">
          <p className="text-ink-soft text-sm">Your sleep, energy, mood and symptom trends live on the Track screen, a summary of your own self reported entries, not a medical assessment.</p>
          <button data-testid="trends-link" onClick={() => navigate("/track")} className="mt-3 inline-flex items-center gap-2 text-brand font-semibold text-sm">
            <LineChart size={16} /> View my trends
          </button>
        </div>
      )}
    </div>
  );
}

function MealBuilder({ slots, value, onChange, keyName }) {
  const obj = value && typeof value === "object" ? value : {};
  const set = (slot, v) => onChange({ ...obj, [slot]: v });
  const togglePlant = (opt) => {
    const cur = Array.isArray(obj.plants) ? obj.plants : [];
    set("plants", cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };
  const Row = ({ label, slot, options }) => (
    <div>
      <div className="text-xs text-ink-muted mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button key={opt} data-testid={`meal-${keyName}-${slot}-${slug(opt)}`} onClick={() => set(slot, opt)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border ${obj[slot] === opt ? "border-[var(--brand)] bg-sage text-ink" : "border-subtle bg-white text-ink-soft"}`}>
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-2.5">
      <Row label="Protein" slot="protein" options={slots.protein} />
      <div>
        <div className="text-xs text-ink-muted mb-1">Plants (choose any)</div>
        <div className="flex flex-wrap gap-1.5">
          {slots.plants.map((opt) => {
            const active = Array.isArray(obj.plants) && obj.plants.includes(opt);
            return (
              <button key={opt} data-testid={`meal-${keyName}-plants-${slug(opt)}`} onClick={() => togglePlant(opt)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border ${active ? "border-[var(--brand)] bg-sage text-ink" : "border-subtle bg-white text-ink-soft"}`}>
                {opt}
              </button>
            );
          })}
        </div>
      </div>
      <Row label="Fiber rich carb" slot="carb" options={slots.carb} />
      <Row label="Healthy fat" slot="fat" options={slots.fat} />
    </div>
  );
}
